import { Injectable } from "@angular/core";
import { BehaviorSubject, throwError, of, Subject, Observable } from "rxjs";
import { catchError, switchMap, finalize, map } from "rxjs/operators";
import {
  AppLoadError,
  ExpiredAgentException,
  IAgentVerificationResult,
  InvalidAgentLoginException,
  InvalidUserInfoException,
  IUser,
} from "../shared/models";
import { AuthService } from "../shared/auth/auth.service";

import { commonEnv } from "src/environments/environment.common";
import { environment } from "src/environments/environment";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class AuthStoreService {
  private readonly _user = new BehaviorSubject<IUser | null>(null);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<string | null>(null);
  private destroyed$ = new Subject<void>();

  // Expose observables for user state, loading, and error
  readonly user$ = this._user.asObservable();
  readonly loading$ = this._loading.asObservable();
  readonly error$ = this._error.asObservable();

  // get current value
  get user(): IUser | null {
    return this._user.getValue();
  }

  private set user(val: IUser | null) {
    this._user.next(val);
  }

  private set loading(val: boolean) {
    this._loading.next(val);
  }

  private set error(val: string | null) {
    this._error.next(val);
  }

  constructor(private authService: AuthService) {}

  selectUser(): Observable<IUser> {
    return this.user$.pipe(map((user) => user));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<string | null> {
    return this.error$.pipe(map((error) => error));
  }

  /**
   * Add a new user to the store.
   * @param agentId - The agent ID, if applicable.
   */
  addNewUser(agentId: string | null = null): IUser {
    const msalUser = this.authService.getUserInfo();

    if (!msalUser) throw new InvalidUserInfoException();
    if (msalUser.tfp === commonEnv.agentSignInPolicy && !agentId) {
      throw new InvalidAgentLoginException();
    }

    if (
      msalUser.tfp === commonEnv.applicantSignInPolicy &&
      localStorage.getItem("msal.login.request") ===
        `${environment.appUrl}/${commonEnv.azureAgentCallbackUrl}`
    ) {
      throw new InvalidAgentLoginException();
    }

    const userInfo: IUser = { ...msalUser, agentId };

    this.user = userInfo; // Update the user state
    return userInfo;
  }

  /**
   * Verify the agent and update the user state.
   */
  verifyAgent() {
    this.loading = true; // Set loading to true
    this.error = null; // Reset error

    return this.authService.verifyAgent().pipe(
      switchMap((verifyResult: IAgentVerificationResult) => {
        if (!verifyResult || Object.entries(verifyResult).length === 0) {
          throw new ExpiredAgentException();
        } else {
          const newUser = this.addNewUser(verifyResult.agentId);
          return of(newUser);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppLoadError(error.id, error.message)
          : new AppLoadError();

        this.error = displayError.message; // Update error state
        return throwError(() => displayError);
      }),
      finalize(() => {
        this.loading = false; // Set loading to false
      })
    );
  }

  /**
   * Handle adding a new user with error handling.
   */
  handleAddUser(agentId: string | null) {
    this.loading = true; // Set loading to true
    this.error = null; // Reset error

    try {
      const newUser = this.addNewUser(agentId);
      this.loading = false; // Set loading to false after success
      return of(newUser);
    } catch (err) {
      this.loading = false; // Ensure loading is false on error
      if (
        err instanceof InvalidAgentLoginException ||
        err instanceof InvalidUserInfoException
      ) {
        this.error = err.message; // Update error state
        return throwError(() => new AppLoadError("Invalid Login", err.message));
      }
      this.error = "An unexpected error occurred"; // Fallback error message
      return throwError(() => err);
    }
  }

  /**
   * Cleanup resources on destruction.
   */
  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
